import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  Eye,
  File as FileIcon,
  FileImage,
  FileText,
  Loader2,
  Paperclip,
  Unlink,
  Upload,
  X,
} from 'lucide-react';

import {
  canPreviewAttachment,
  isImageAttachment,
  isPdfAttachment,
} from '#/api/platform/attachments';
import { cn } from '#/lib/utils';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  ATTACHMENT_LIST_QUERY,
  documentAttachmentsQueryKey,
  formatAttachmentSize,
  mergePendingAttachmentPick,
  summarizeUploadAttachmentResults,
  uploadAttachmentFiles,
  validateAttachmentPick,
} from '#/modules/commonComponents/attachments/attachmentHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { getRequestTransportErrorMessage } from '#/utils/requestError';
import { triggerBlobDownload } from '#/utils/downloadBlob';
import { toast } from '#/utils/toast';

const GRID_CLASS = cn(
  'm-0 mt-3 grid list-none grid-cols-1 gap-3 p-0',
  'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
);

const ITEM_CARD_CLASS = cn(
  'flex min-h-80 flex-col overflow-hidden rounded-lg border border-slate-200',
  'bg-white shadow-xs',
);

const PREVIEW_AREA_CLASS = cn(
  'flex h-56 w-full items-center justify-center overflow-hidden',
  'border-b border-slate-200 bg-gray-50',
);

const STAGED_GRID_CLASS = cn(
  'm-0 grid list-none grid-cols-1 gap-3 p-0',
  'sm:grid-cols-2 lg:grid-cols-3',
);

function getAttachmentErrorMessage(error, fallback) {
  const transport = getRequestTransportErrorMessage(error);
  if (transport) {
    return transport;
  }
  return error?.response?.data?.message || error?.message || fallback;
}

function resolveVersion(item) {
  const version = item?.version ?? item?.currentVersion;
  return version == null || version === '' ? null : version;
}

function toastUploadResults(results) {
  const { okCount, failCount } = summarizeUploadAttachmentResults(results);
  if (okCount > 0) {
    toast.success(failCount > 0
      ? `已上傳 ${okCount} 個附件，${failCount} 個失敗`
      : `已上傳 ${okCount} 個附件`);
    return;
  }
  if (failCount > 0) {
    toast.error('附件上傳失敗');
  }
}

function AttachmentTypeIcon({ contentType, filename, className }) {
  if (isPdfAttachment(contentType, filename)) {
    return <FileText className={cn('size-10 text-red-500', className)} aria-hidden="true" />;
  }
  if (isImageAttachment(contentType, filename)) {
    return <FileImage className={cn('size-10 text-sky-500', className)} aria-hidden="true" />;
  }
  return <FileIcon className={cn('size-10 text-gray-500', className)} aria-hidden="true" />;
}

AttachmentTypeIcon.propTypes = {
  contentType: PropTypes.string,
  filename: PropTypes.string,
  className: PropTypes.string,
};

/** 尚未上傳的圖片：以本機暫存網址顯示縮圖，卸載時釋放。 */
function PendingImageThumb({ file }) {
  const imgRef = useRef(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    if (imgRef.current) {
      imgRef.current.src = objectUrl;
    }
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return <img ref={imgRef} alt="" className="size-full object-cover" />;
}

PendingImageThumb.propTypes = {
  file: PropTypes.instanceOf(File).isRequired,
};

/** 已上傳圖片：打 preview API 顯示縮圖。 */
function ServerImageThumb({
  api,
  businessId,
  fileId,
  version,
  contentType,
  filename,
}) {
  const imgRef = useRef(null);
  const query = useQuery({
    queryKey: ['erp', 'documentAttachmentThumb', businessId, fileId, version],
    queryFn: () => api.fetchPreviewBlob(businessId, fileId, version, filename),
    enabled: Boolean(api && businessId && fileId && version != null),
    staleTime: 5 * 60 * 1000,
  });
  const blob = query.data?.blob;

  useEffect(() => {
    if (!blob || !imgRef.current) {
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    imgRef.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (query.isLoading) {
    return (
      <Loader2
        className="size-8 animate-spin text-muted-foreground"
        aria-hidden="true"
      />
    );
  }

  if (query.isError || !blob) {
    return <AttachmentTypeIcon contentType={contentType} filename={filename} />;
  }

  return <img ref={imgRef} alt="" className="size-full object-cover" />;
}

ServerImageThumb.propTypes = {
  api: PropTypes.shape({
    fetchPreviewBlob: PropTypes.func.isRequired,
  }).isRequired,
  businessId: PropTypes.string.isRequired,
  fileId: PropTypes.string.isRequired,
  version: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  contentType: PropTypes.string,
  filename: PropTypes.string,
};

function AttachmentCardActions({ children }) {
  return (
    <div className="mt-auto flex flex-wrap gap-1.5 border-t border-slate-100 px-3 py-2">
      {children}
    </div>
  );
}

AttachmentCardActions.propTypes = {
  children: PropTypes.node,
};

/** 圖示＋文字按鈕；手機只留圖示省寬度。 */
function CardActionButton({ icon: Icon, label, loading, className, ...props }) {
  return (
    <Button
      type="button"
      size="sm"
      className={cn('max-sm:size-8 max-sm:px-0', className)}
      aria-label={label}
      title={label}
      {...props}
    >
      {loading ? (
        <Loader2 className="animate-spin" aria-hidden="true" />
      ) : (
        <Icon aria-hidden="true" />
      )}
      <span className="max-sm:sr-only">{label}</span>
    </Button>
  );
}

CardActionButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

function AttachmentItemCard({
  name,
  dateLabel,
  sizeLabel,
  previewable,
  busy,
  onPreview,
  previewContent,
  actions,
}) {
  return (
    <li className={ITEM_CARD_CLASS}>
      {previewable ? (
        <button
          type="button"
          className={cn(
            PREVIEW_AREA_CLASS,
            'cursor-pointer transition-colors hover:bg-gray-100',
            'focus-visible:outline-2 focus-visible:outline-offset-[-2px]',
            'focus-visible:outline-primary-400',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
          disabled={busy}
          onClick={onPreview}
          aria-label={`預覽 ${name}`}
          title="點擊預覽"
        >
          {previewContent}
        </button>
      ) : (
        <div className={PREVIEW_AREA_CLASS}>{previewContent}</div>
      )}
      <div className="min-w-0 space-y-0.5 px-3 py-2.5">
        <TruncatedTextWithTooltip
          text={name || '—'}
          className="block max-w-full text-sm font-medium text-gray-900"
        />
        <TruncatedTextWithTooltip
          text={dateLabel || '—'}
          className="block max-w-full text-xs text-muted-foreground"
        />
        <TruncatedTextWithTooltip
          text={sizeLabel || '—'}
          className="block max-w-full text-xs text-muted-foreground"
        />
      </div>
      {actions ? <AttachmentCardActions>{actions}</AttachmentCardActions> : null}
    </li>
  );
}

AttachmentItemCard.propTypes = {
  name: PropTypes.string,
  dateLabel: PropTypes.string,
  sizeLabel: PropTypes.string,
  previewable: PropTypes.bool,
  busy: PropTypes.bool,
  onPreview: PropTypes.func,
  previewContent: PropTypes.node,
  actions: PropTypes.node,
};

const DocumentAttachmentsCard = ({
  businessId,
  ownerKey,
  api,
  policy,
  title = '附件',
  description = null,
  readOnly = false,
  variant = 'card',
  className,
  pendingFiles = null,
  onPendingFilesChange = null,
}) => {
  const queryClient = useQueryClient();
  const dialogFileInputRef = useRef(null);
  /** 關閉預覽時，巢狀 Dialog 常連帶觸發外層 onOpenChange(false)；短暫擋掉。 */
  const closingPreviewRef = useRef(false);
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [stagedFiles, setStagedFiles] = useState([]);

  const isPendingMode = !businessId && typeof onPendingFilesChange === 'function';
  const enabled = isPendingMode
    ? true
    : Boolean(businessId && api && ownerKey);
  const queryKey = documentAttachmentsQueryKey(ownerKey, businessId);
  const pendingList = Array.isArray(pendingFiles) ? pendingFiles : [];

  const listQuery = useQuery({
    queryKey,
    queryFn: () => api.fetchList(businessId, ATTACHMENT_LIST_QUERY),
    enabled: !isPendingMode && enabled,
  });

  useEffect(() => () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  const uploadMutation = useMutation({
    mutationFn: (files) => uploadAttachmentFiles(api, businessId, files),
    onSuccess: async (results) => {
      toastUploadResults(results);
      await queryClient.invalidateQueries({ queryKey });
      setStagedFiles([]);
      setUploadDialogOpen(false);
    },
    onError: (error) => {
      toast.error(getAttachmentErrorMessage(error, '附件上傳失敗'));
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (fileId) => api.unlink(businessId, fileId),
    onSuccess: async (result) => {
      toast.success(result?.message || '已解除附件');
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      toast.error(getAttachmentErrorMessage(error, '解除附件失敗'));
    },
  });

  const isBusy = uploadMutation.isPending || unlinkMutation.isPending || Boolean(busyAction);

  function clearUploadDialog() {
    setStagedFiles([]);
    if (dialogFileInputRef.current) {
      dialogFileInputRef.current.value = '';
    }
  }

  function handleOpenUploadDialog() {
    clearUploadDialog();
    setUploadDialogOpen(true);
  }

  function handleUploadDialogOpenChange(nextOpen) {
    if (nextOpen) {
      setUploadDialogOpen(true);
      return;
    }
    // 預覽 Dialog 關閉／點外側時，勿一併關掉上傳 Dialog
    if (preview || closingPreviewRef.current) {
      return;
    }
    if (uploadMutation.isPending) {
      return;
    }
    clearUploadDialog();
    setUploadDialogOpen(false);
  }

  function handleDialogFileChange(event) {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) {
      return;
    }
    const validated = mergePendingAttachmentPick(stagedFiles, picked);
    if (!validated.ok) {
      toast.error(validated.message);
      return;
    }
    setStagedFiles(validated.files);
  }

  function handleRemoveStaged(index) {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleConfirmUpload() {
    if (stagedFiles.length === 0) {
      toast.error('請選擇檔案');
      return;
    }

    if (isPendingMode) {
      const validated = mergePendingAttachmentPick(pendingList, stagedFiles);
      if (!validated.ok) {
        toast.error(validated.message);
        return;
      }
      onPendingFilesChange(validated.files);
      clearUploadDialog();
      setUploadDialogOpen(false);
      return;
    }

    const validated = validateAttachmentPick(stagedFiles);
    if (!validated.ok) {
      toast.error(validated.message);
      return;
    }
    uploadMutation.mutate(validated.files);
  }

  function handleRemovePending(index) {
    if (!isPendingMode) {
      return;
    }
    onPendingFilesChange(pendingList.filter((_, i) => i !== index));
  }

  function openPreview(blob, meta) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(blob));
    setPreview(meta);
  }

  function handlePreviewPending(file) {
    if (!canPreviewAttachment(file.type, file.name)) {
      toast.error('此類型無法瀏覽');
      return;
    }
    openPreview(file, { originalFilename: file.name, contentType: file.type });
  }

  async function handlePreview(item) {
    const fileId = item?.fileId;
    const version = resolveVersion(item);
    if (!fileId || version == null) {
      toast.error('此附件無法瀏覽');
      return;
    }
    if (!canPreviewAttachment(item.contentType, item.originalFilename)) {
      toast.error('此類型僅能下載，無法瀏覽');
      return;
    }

    setBusyAction(`preview:${fileId}`);
    try {
      const result = await api.fetchPreviewBlob(
        businessId,
        fileId,
        version,
        item.originalFilename,
      );
      openPreview(result.blob, {
        ...item,
        contentType: result.contentType || item.contentType,
      });
    } catch (error) {
      toast.error(getAttachmentErrorMessage(error, '無法開啟預覽'));
    } finally {
      setBusyAction(null);
    }
  }

  async function handleDownload(item) {
    const fileId = item?.fileId;
    const version = resolveVersion(item);
    if (!fileId || version == null) {
      toast.error('此附件無法下載');
      return;
    }

    setBusyAction(`download:${fileId}`);
    try {
      const result = await api.fetchDownloadBlob(
        businessId,
        fileId,
        version,
        item.originalFilename,
      );
      triggerBlobDownload(result.blob, result.filename || item.originalFilename);
    } catch (error) {
      toast.error(getAttachmentErrorMessage(error, '下載失敗'));
    } finally {
      setBusyAction(null);
    }
  }

  function handleClosePreview(nextOpen) {
    if (nextOpen) {
      return;
    }
    closingPreviewRef.current = true;
    setPreview(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    window.setTimeout(() => {
      closingPreviewRef.current = false;
    }, 0);
  }

  if (!enabled) {
    return null;
  }

  const attachments = listQuery.data?.data ?? [];
  const itemCount = isPendingMode ? pendingList.length : attachments.length;
  const accept = policy?.accept || '';
  const policyLabel = policy?.label || '允許的檔案類型';
  const defaultDescription = isPendingMode
    ? `可先選取 ${policyLabel}，儲存單據後才會上傳。一次最多 10 個、合計 100 MB。`
    : `可上傳 ${policyLabel}。一次最多 10 個、合計 100 MB。`;
  const uploadButtonLabel = isPendingMode ? '選擇檔案' : '上傳附件';
  const confirmUploadLabel = isPendingMode
    ? '確認加入'
    : (uploadMutation.isPending ? '上傳中…' : '確認上傳');

  return (
    <section
      className={cn(
        variant === 'card'
          && 'rounded-lg border border-slate-200 bg-panel p-4 shadow-xs',
        className,
      )}
    >
      <div className="form-block mb-0!">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="form-block__title mb-0! flex min-w-0 items-center gap-2">
            <Paperclip className="size-4 shrink-0 text-primary-400" aria-hidden="true" />
            <span>{title}</span>
            {itemCount > 0 ? (
              <span
                className={cn(
                  'rounded-full bg-gray-100 px-2 py-0.5',
                  'text-xs font-medium tabular-nums text-gray-700',
                )}
              >
                {itemCount}
              </span>
            ) : null}
          </h2>
          {!readOnly ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={handleOpenUploadDialog}
            >
              <Upload aria-hidden="true" />
              {uploadButtonLabel}
            </Button>
          ) : null}
        </div>
        <p className="form-block__description">
          {description || defaultDescription}
        </p>
      </div>

      {isPendingMode ? (
        pendingList.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">尚未選取附件。</p>
        ) : (
          <ul className={GRID_CLASS}>
            {pendingList.map((file, index) => {
              const name = file.name || '—';
              const previewable = canPreviewAttachment(file.type, file.name);
              const isImage = isImageAttachment(file.type, file.name);
              return (
                <AttachmentItemCard
                  key={`${file.name}-${file.size}-${index}`}
                  name={name}
                  dateLabel="待上傳"
                  sizeLabel={formatAttachmentSize(file.size)}
                  previewable={previewable}
                  onPreview={() => handlePreviewPending(file)}
                  previewContent={
                    isImage ? (
                      <PendingImageThumb file={file} />
                    ) : (
                      <AttachmentTypeIcon contentType={file.type} filename={file.name} />
                    )
                  }
                  actions={(
                    <>
                      {previewable ? (
                        <CardActionButton
                          icon={Eye}
                          label="瀏覽"
                          variant="outline"
                          onClick={() => handlePreviewPending(file)}
                        />
                      ) : null}
                      {!readOnly ? (
                        <CardActionButton
                          icon={X}
                          label="移除"
                          variant="ghost"
                          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                          onClick={() => handleRemovePending(index)}
                        />
                      ) : null}
                    </>
                  )}
                />
              );
            })}
          </ul>
        )
      ) : (
        <>
          {listQuery.isLoading ? (
            <p className="mt-3 text-sm text-muted-foreground">附件載入中…</p>
          ) : null}

          {listQuery.isError ? (
            <p className="mt-3 text-sm text-destructive">
              {getAttachmentErrorMessage(listQuery.error, '附件載入失敗')}
            </p>
          ) : null}

          {!listQuery.isLoading && !listQuery.isError && attachments.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">尚無附件。</p>
          ) : null}

          {attachments.length > 0 ? (
            <ul className={GRID_CLASS}>
              {attachments.map((item) => {
                const fileId = item.fileId;
                const name = item.originalFilename || fileId || '—';
                const version = resolveVersion(item);
                const previewable = canPreviewAttachment(
                  item.contentType,
                  item.originalFilename,
                );
                const isImage = isImageAttachment(
                  item.contentType,
                  item.originalFilename,
                );
                const isPreviewing = busyAction === `preview:${fileId}`;
                const isDownloading = busyAction === `download:${fileId}`;
                const isUnlinking = unlinkMutation.isPending
                  && unlinkMutation.variables === fileId;

                return (
                  <AttachmentItemCard
                    key={fileId || item.originalFilename}
                    name={name}
                    dateLabel={item.uploadedAt || '—'}
                    sizeLabel={formatAttachmentSize(item.sizeBytes)}
                    previewable={previewable}
                    busy={isBusy}
                    onPreview={() => handlePreview(item)}
                    previewContent={
                      isPreviewing ? (
                        <Loader2
                          className="size-8 animate-spin text-muted-foreground"
                          aria-hidden="true"
                        />
                      ) : isImage && version != null ? (
                        <ServerImageThumb
                          api={api}
                          businessId={String(businessId)}
                          fileId={String(fileId)}
                          version={version}
                          contentType={item.contentType}
                          filename={item.originalFilename}
                        />
                      ) : (
                        <AttachmentTypeIcon
                          contentType={item.contentType}
                          filename={item.originalFilename}
                        />
                      )
                    }
                    actions={(
                      <>
                        {previewable ? (
                          <CardActionButton
                            icon={Eye}
                            label="瀏覽"
                            variant="outline"
                            loading={isPreviewing}
                            disabled={isBusy}
                            onClick={() => handlePreview(item)}
                          />
                        ) : null}
                        <CardActionButton
                          icon={Download}
                          label="下載"
                          variant="outline"
                          loading={isDownloading}
                          disabled={isBusy}
                          onClick={() => handleDownload(item)}
                        />
                        {!readOnly ? (
                          <CardActionButton
                            icon={Unlink}
                            label="解除"
                            variant="ghost"
                            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                            loading={isUnlinking}
                            disabled={isBusy}
                            onClick={() => unlinkMutation.mutate(fileId)}
                          />
                        ) : null}
                      </>
                    )}
                  />
                );
              })}
            </ul>
          ) : null}
        </>
      )}

      <AppDialog
        open={uploadDialogOpen}
        onOpenChange={handleUploadDialogOpenChange}
        title={isPendingMode ? '選擇附件' : '上傳附件'}
        description={
          isPendingMode
            ? `選取後按確認加入；儲存單據後才會上傳。可選 ${policyLabel}，一次最多 10 個、合計 100 MB。`
            : `確認後才會上傳。可選 ${policyLabel}，一次最多 10 個、合計 100 MB。`
        }
        headerVariant="muted"
        maxWidthClassName="sm:max-w-4xl"
        closeOnOutsideClick={false}
        closeOnEscape={!uploadMutation.isPending && !preview}
        onCancel={() => handleUploadDialogOpenChange(false)}
        onConfirm={handleConfirmUpload}
        confirmText={confirmUploadLabel}
        confirmDisabled={stagedFiles.length === 0 || uploadMutation.isPending}
        cancelText="取消"
      >
        <div className="space-y-3">
          <div>
            <input
              ref={dialogFileInputRef}
              type="file"
              multiple
              accept={accept}
              className="sr-only"
              tabIndex={-1}
              disabled={uploadMutation.isPending}
              onChange={handleDialogFileChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploadMutation.isPending}
              onClick={() => dialogFileInputRef.current?.click()}
            >
              <Upload aria-hidden="true" />
              加入檔案
            </Button>
          </div>

          {stagedFiles.length === 0 ? (
            <p className="text-sm text-muted-foreground">尚未選擇檔案。</p>
          ) : (
            <ul className={STAGED_GRID_CLASS}>
              {stagedFiles.map((file, index) => {
                const name = file.name || '—';
                const previewable = canPreviewAttachment(file.type, file.name);
                const isImage = isImageAttachment(file.type, file.name);
                return (
                  <AttachmentItemCard
                    key={`${file.name}-${file.size}-${index}`}
                    name={name}
                    dateLabel="待確認"
                    sizeLabel={formatAttachmentSize(file.size)}
                    previewable={previewable}
                    busy={uploadMutation.isPending}
                    onPreview={() => handlePreviewPending(file)}
                    previewContent={
                      isImage ? (
                        <PendingImageThumb file={file} />
                      ) : (
                        <AttachmentTypeIcon
                          contentType={file.type}
                          filename={file.name}
                        />
                      )
                    }
                    actions={(
                      <>
                        {previewable ? (
                          <CardActionButton
                            icon={Eye}
                            label="預覽"
                            variant="outline"
                            disabled={uploadMutation.isPending}
                            onClick={() => handlePreviewPending(file)}
                          />
                        ) : null}
                        <CardActionButton
                          icon={X}
                          label="移除"
                          variant="ghost"
                          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                          disabled={uploadMutation.isPending}
                          onClick={() => handleRemoveStaged(index)}
                        />
                      </>
                    )}
                  />
                );
              })}
            </ul>
          )}
        </div>
      </AppDialog>

      <AppDialog
        open={Boolean(preview)}
        onOpenChange={handleClosePreview}
        title={preview?.originalFilename || '附件預覽'}
        description="圖片與 PDF 可於此瀏覽；關閉後回到上一層。"
        headerVariant="muted"
        maxWidthClassName="sm:max-w-4xl"
        closeOnOutsideClick
        closeOnEscape
        onCancel={() => handleClosePreview(false)}
        cancelText="關閉"
      >
        {preview && previewUrl ? (
          isPdfAttachment(preview.contentType, preview.originalFilename) ? (
            <iframe
              title={preview.originalFilename || 'PDF 預覽'}
              src={previewUrl}
              className="h-[70vh] w-full rounded border border-slate-200 bg-white"
            />
          ) : isImageAttachment(preview.contentType, preview.originalFilename) ? (
            <div className="flex max-h-[70vh] items-center justify-center overflow-auto">
              <img
                src={previewUrl}
                alt={preview.originalFilename || '圖片預覽'}
                className="max-h-[70vh] max-w-full object-contain"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">無法預覽此類型。</p>
          )
        ) : (
          <p className="text-sm text-muted-foreground">載入預覽中…</p>
        )}
      </AppDialog>
    </section>
  );
};

DocumentAttachmentsCard.propTypes = {
  businessId: PropTypes.string,
  ownerKey: PropTypes.string.isRequired,
  api: PropTypes.shape({
    createUploadBatch: PropTypes.func.isRequired,
    upload: PropTypes.func.isRequired,
    fetchList: PropTypes.func.isRequired,
    unlink: PropTypes.func.isRequired,
    fetchPreviewBlob: PropTypes.func.isRequired,
    fetchDownloadBlob: PropTypes.func.isRequired,
  }).isRequired,
  policy: PropTypes.shape({
    id: PropTypes.string,
    accept: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  readOnly: PropTypes.bool,
  /** card：獨立卡片（詳情頁）；section：嵌在 create/edit form 內，不加外框。 */
  variant: PropTypes.oneOf(['card', 'section']),
  className: PropTypes.string,
  pendingFiles: PropTypes.arrayOf(PropTypes.instanceOf(File)),
  onPendingFilesChange: PropTypes.func,
};

export default DocumentAttachmentsCard;
